import React, { useEffect, useState } from "react";
import { 
	Skeleton,
	message,
	List,
	Drawer,
	Space,
	Popconfirm,
	Transfer,
	Descriptions,
	Input,
	Button,
	Row,
	Col,
	theme
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { get_users, edit_user, get_organizations } from "../services";
import PropTypes from "prop-types";
import { CircleAnimationCompact } from "../components/loading";
import { useCalculateListSizeForPaginationHook } from "../hooks";
import { hover_item } from "../hover";

const no_selected_user = {
	name: "",
	email: "",
	assigned_organizations: [],
};

export const AdminConfig = ({ token }) => {

	const { token: antToken } = theme.useToken();

	const [users, setUsers] = useState([]);
	const [organizations, setOrganizations] = useState([]);
	const [selected, setSelected] = useState(no_selected_user);
	const [selectedKeys, setSelectedKeys] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadingSubmit, setLoadingSubmit] = useState(false);
	const { pageSize } = useCalculateListSizeForPaginationHook(70);
	const [openEdit, setOpenEdit] = useState(false);
	const [hoveredItem, setHoveredItem] = useState(null);

	const fetchAssignedOrganizations = (user) => {
		// Verifica si user.assigned_organizations es un array válido
		if (!Array.isArray(user.assigned_organizations) || user.assigned_organizations.length === 0) {
			return [];
		}
	
		const assignedOrgs = organizations
			.filter(org => user.assigned_organizations.includes(org.org_id))
			.map(org => org.id);
	
		return assignedOrgs;
	};
	

	const showEdit = async (item) => {
		const assignedOrganizations = await fetchAssignedOrganizations(item);
		setSelected({ ...item, assigned_organizations: assignedOrganizations });
		setOpenEdit(true);
	};

	const onClose = () => {
		setOpenEdit(false);
		setSelected(no_selected_user);
	};

	const handleChangeUser = (value, attribute) => {
		setSelected((userSelected) => ({
			...userSelected, [attribute]: value
		}));
	};

	const handleChangeAssignedOrganizations = (newTargetKeys) => {
		setSelected({
			...selected,
			assigned_organizations: newTargetKeys
		});
	};

	const handleSelectChangeOrganizations = (sourceSelectedKeys, targetSelectedKeys) => {
		setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys]);
	};

	const submitChanges = async () => {
		setLoadingSubmit(true);
    
		const user_notification = {
			email: selected.email,
			assigned_organizations: selected.assigned_organizations.map(orgId => {
				const org = organizations.find(o => o.id === orgId);
				return org ? org.org_id : null;
			}).filter(orgId => orgId !== null),
		};
    
		try {
			const res = await edit_user(token, user_notification, "gen");
			if (res) {
				message.success("Edited Correctly");
    
				setUsers(users.map(user => {
					if (user.email === selected.email) {
						return selected;
					}
					return user;
				}));            
    
				onClose();
			}
		} catch(error){
			console.error(error);
			message.error(error.message);
			onClose();
		}
    
		setLoadingSubmit(false);
	};

	const reduceText = (text) => {
		if (text === "") {
			return "---";
		}
		if (text.length > 40) {
			return text.substring(0, 40) + "...";
		}
		return text;
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				// Ejecuta ambas solicitudes en paralelo
				const [usersRes, orgs] = await Promise.all([
					get_users(token, "gen"),
					get_organizations(token)
				]);
				
				// Procesa la respuesta de usuarios y los ordena alfabéticamente por 'name'
				const sortedUsers = usersRes.sort((a, b) => {
					if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
					if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
					return 0;
				});
				setUsers(sortedUsers);
				
				// Procesa la respuesta de organizaciones
				const formated_organizations = orgs.map((organization) => ({
					id: organization.id,
					name: organization.name,
					org_id: organization.org_id,
					key: organization.id,
					disabled: false
				}));
				setOrganizations(formated_organizations);
	
				setLoading(false);
			} catch (error) {
				console.error(error.response?.data);
				message.error(error.response?.data?.message || "Error al cargar datos");
				setLoading(false);
			}
		};
	
		fetchData();
	}, []); // Asegúrate de incluir token en las dependencias si es necesario.
	

	return (
		<>
			{
				loading &&
			<CircleAnimationCompact/>
			}
			{
				!loading &&
			<>
				<List
					pagination={{
						position: "top",
						align: "center",
						pageSize: pageSize,
						showSizeChanger: false,
						disabled: false,
					}}
					header={
						<Row
							style={{
								background: antToken.colorBgLayout,
								borderRadius: "10px"
							}}
						>
							<Col flex="auto">
								<List.Item key={0} >
									<List.Item.Meta 
										title="Nombre"
									/>
									<List.Item.Meta  
										title="Correo Electrónico" 
									/>
								</List.Item>
							</Col>
						</Row>
					}
					dataSource={users}
					renderItem={(item, index) => (
						<List.Item
							key={index}
							onMouseEnter={() => setHoveredItem(index)}
							onMouseLeave={() => setHoveredItem(null)}
							style={hover_item(hoveredItem === index, antToken, "invoices", 45)}
							onClick={() => showEdit(item)}
						>
							<Skeleton avatar title={false} loading={loading} active>
								<List.Item.Meta
									title={reduceText(item.name)}
								/>
								<List.Item.Meta
									description={reduceText(item.email)}
								/>
							</Skeleton>
						</List.Item>
					)}
				/>
			</>
			}

			<Drawer
				title="Editar Usuario"
				placement="right"
				width={800}
				onClose={onClose}
				open={openEdit}
				extra={
					<Space>
						<Popconfirm
							title="Desactivar Usuario"
							description="¿Estás seguro que quieres desactivar este usuario?"
							onConfirm={onClose} // Aquí puedes agregar la función para desactivar
							icon={<QuestionCircleOutlined style={{ color: "red" }} />}
						>
							<Button type="primary" loading={loadingSubmit} danger>
                                Desactivar
							</Button>
						</Popconfirm>
						<Button type="primary" loading={loadingSubmit} onClick={submitChanges}>
                            Editar
						</Button>
					</Space>
				}
			>
				<Descriptions
					layout="vertical"
					labelStyle={{
						//color: "black",
						fontWeight: "bold",
					}}
				>
					<Descriptions.Item label={"Nombre"}>
						<Input
							rows={1}
							showCount
							status={(selected.name.trim() === "") ? "error" : ""}
							value={selected.name}
							onChange={(event) => handleChangeUser(event.target.value, "name")}
							maxLength={15}
						/>
					</Descriptions.Item>
					<Descriptions.Item label={"Correo"}>
						<Input
							rows={1}
							showCount
							status={(selected.email.trim() === "") ? "error" : ""}
							value={selected.email}
							onChange={(event) => handleChangeUser(event.target.value, "email")}
							maxLength={30}
						/>
					</Descriptions.Item>
				</Descriptions>
				<div>
					<h3 style={{ color: "#808080" }}>Organizaciones Asignadas:</h3>
					<Transfer
						dataSource={organizations}
						listStyle={{
							width: 350,
							height: 300,
						}}
						showSearch
						filterOption={(inputValue, item) =>
							item.name.toLowerCase().includes(inputValue.toLowerCase())
						}
						titles={["Organizaciones Disponibles", "Organizaciones Asignadas"]}
						targetKeys={selected.assigned_organizations}
						selectedKeys={selectedKeys}
						onChange={handleChangeAssignedOrganizations}
						onSelectChange={handleSelectChangeOrganizations}
						render={(item) => item.name}
						style={{
							marginBottom: 16,
							marginTop: "10px"
						}}
					/>
				</div>
			</Drawer>
		</>
	);
};

AdminConfig.propTypes = {
	token: PropTypes.string.isRequired,
};

